import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight, Download, Loader2,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import {
  PIPELINE_ACTIVITY_REPORT_URL, USERS_SIMPLE_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import StateFilterSelect from "../../components/common/StateFilterSelect";
import { STAGE_BADGE } from "../../utils/leadStageBadge";
import { cn } from "../../utils/tailwindUtils";
import TruncatedText from "../../components/ui/truncated-text";
import { formatDateTime } from "../../utils/datetime";

// Only pipeline stages where a deal is actively being negotiated/closed
const PIPELINE_STAGES = ["Proposal", "Negotiation"];

const DATE_PRESETS = [
  { value: "all",        label: "All Time" },
  { value: "last_week",  label: "Last 7 Days" },
  { value: "last_month", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "custom",     label: "Custom Range" },
];

const resolveDateRange = (preset, customFrom, customTo) => {
  const today = dayjs();
  const fmt = (d) => d.format("DD-MM-YYYY");
  switch (preset) {
    case "last_week":  return { from_date: fmt(today.subtract(7,  "day")), to_date: fmt(today) };
    case "last_month": return { from_date: fmt(today.subtract(30, "day")), to_date: fmt(today) };
    case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
    case "custom":
      return (customFrom && customTo)
        ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"), to_date: dayjs(customTo).format("DD-MM-YYYY") }
        : {};
    default: return {};
  }
};

const fmtCurrency = (v) => {
  const n = parseFloat(v || 0);
  return n ? `₹${n.toLocaleString("en-IN")}` : "—";
};

const PipelineActivityReport = () => {
  const { fetch } = useApiMethods();
  const navigate = useNavigate();

  const [allData, setAllData]         = useState([]);
  const [summary, setSummary]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [stageFilter, setStageFilter] = useState(null);
  const [datePreset, setDatePreset]   = useState("all");
  const [customFrom, setCustomFrom]   = useState("");
  const [customTo, setCustomTo]       = useState("");
  const [users, setUsers]             = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(25);
  const [exporting, setExporting]     = useState(false);

  /** Server-side filters. `withUiFilters` also applies the stage pill + search box
   *  so an export matches exactly what the user is looking at. */
  const buildParams = (withUiFilters = false) => {
    const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
    const params = new URLSearchParams();
    // Always restrict to Proposal + Negotiation
    params.append("lead_stage", withUiFilters && stageFilter ? stageFilter : PIPELINE_STAGES.join(","));
    if (from_date)      params.append("from_date", from_date);
    if (to_date)        params.append("to_date",   to_date);
    if (selectedUserId) params.append("assigned_to_id", selectedUserId);
    if (stateFilter) params.append("state", stateFilter);
    if (withUiFilters && search.trim()) params.append("search", search.trim());
    return params;
  };

  const load = useCallback(() => {
    if (datePreset === "custom" && (!customFrom || !customTo)) return;
    setLoading(true);
    const url = `${PIPELINE_ACTIVITY_REPORT_URL}?${buildParams().toString()}`;
    fetch(url)
      .then(res => {
        if (res?.status === "success") {
          setAllData(res.data || []);
          setSummary(res.summary || null);
        } else {
          ToastMsg("error", res?.message || "Failed to load report");
        }
      })
      .catch(() => ToastMsg("error", "Failed to load report"))
      .finally(() => setLoading(false));
  }, [datePreset, customFrom, customTo, selectedUserId, stateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const params = buildParams(true);
      params.append("export_type", "excel");
      const data = await apiClient.get(`${PIPELINE_ACTIVITY_REPORT_URL}?${params.toString()}`, {
        responseType: "blob",
        timeout: 300000,
      });
      const blob = data instanceof Blob
        ? data
        : new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Pipeline_Activity_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      ToastMsg("error", "Could not generate the Excel export");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetch(USERS_SIMPLE_URL).then(res => {
      if (res?.status === "success") setUsers(res.data || []);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stageCounts = useMemo(() => {
    const counts = {};
    allData.forEach(r => { if (r.lead_stage) counts[r.lead_stage] = (counts[r.lead_stage] || 0) + 1; });
    return counts;
  }, [allData]);

  const filtered = useMemo(() => {
    let rows = allData;
    if (stageFilter) rows = rows.filter(r => r.lead_stage === stageFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        r.customer_name?.toLowerCase().includes(q) ||
        r.contact_number?.toLowerCase().includes(q) ||
        r.assigned_to_name?.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [allData, stageFilter, search]);

  useEffect(() => { setPage(1); }, [stageFilter, search, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <ContainerCard>
      {/* ── fixed top: header + filters ── */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-4 px-1">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Pipeline Activity Report</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Active deals in Proposal & Negotiation — value, expected close and followup activity
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={downloadExcel} size="sm" variant="outline" className="flex items-center gap-1 text-xs"
              disabled={exporting || loading || !filtered.length}>
              {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
              {exporting ? "Preparing…" : "Export Excel"}
            </Button>
            <Button onClick={load} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
              <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* date + owner filter */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative">
            <select
              value={datePreset}
              onChange={e => {
                setDatePreset(e.target.value);
                if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
              }}
              className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
            >
              {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          </div>

          {datePreset === "custom" && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
              <Button onClick={() => load()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
            </>
          )}

          <div className="relative">
            <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
            >
              <option value="">All Reps</option>
              {users.map(u => {
                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                return <option key={u.id} value={u.id}>{name}</option>;
              })}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          </div>
          <StateFilterSelect value={stateFilter} onChange={setStateFilter} />

          {summary && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-auto">
              Pipeline: <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtCurrency(summary.total_pipeline_value)}</span> ·
              Avg Age: <span className="font-semibold text-gray-700 dark:text-gray-200">{summary.avg_age_days}d</span> ·
              Stale: <span className="font-semibold text-amber-600 dark:text-amber-400">{summary.stale_count}</span>
            </span>
          )}
        </div>

        {/* stage filter pills + search */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={() => setStageFilter(null)}
            className={cn(
              "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
              !stageFilter
                ? "bg-gray-700 text-white border-gray-800"
                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            All · {allData.length}
          </button>

          {PIPELINE_STAGES.map(stage => (
            <button
              key={stage}
              onClick={() => setStageFilter(stageFilter === stage ? null : stage)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                stageFilter === stage
                  ? "bg-indigo-600 text-white border-indigo-700"
                  : cn(
                      STAGE_BADGE[stage] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
                      "border-transparent hover:opacity-90"
                    )
              )}
            >
              {stage} · {stageCounts[stage] ?? 0}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
            <Search className="h-3 w-3 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Customer, mobile or rep…"
              className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-36"
            />
          </div>
        </div>
      </div>

      {/* ── scrollable table + fixed pagination ── */}
      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading pipeline…</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Created</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Age</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Mobile No</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Stage</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Pipeline Value</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Expected Close</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Lead Source</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Lead Potential</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-center">Followups</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Last Followup</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Days Since</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Next Followup</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Latest Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginated.map((row, idx) => (
                    <tr
                      key={row.customer_id}
                      className={cn(
                        "hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors",
                        row.is_stale && "bg-amber-50/40 dark:bg-amber-900/10"
                      )}
                      onClick={() => navigate("/customers/customerdetails", {
                        state: { formEdit: true, row: { id: row.customer_id } },
                      })}
                    >
                      <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDateTime(row.created_date)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {row.age_days ?? 0}d
                      </td>
                      <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200 max-w-[180px]">
    <TruncatedText text={row.customer_name} />
</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {row.contact_number || "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-[10px] font-medium",
                          STAGE_BADGE[row.lead_stage] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                        )}>
                          {row.lead_stage || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                        {fmtCurrency(row.pipeline_value)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {row.expected_close_date
                          ? dayjs(row.expected_close_date).format("DD MMM YYYY")
                          : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[120px] truncate">
                        {row.lead_source || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[120px] truncate">
                        {row.lead_potential || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[140px] truncate">
                        {row.assigned_to_name || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          {row.followup_count || 0}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {row.last_followup_date
                          ? dayjs(row.last_followup_date).format("DD MMM YYYY")
                          : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className={cn(
                        "px-3 py-2.5 whitespace-nowrap",
                        row.days_since_followup > 14 ? "text-red-500 font-medium" : "text-gray-500 dark:text-gray-400"
                      )}>
                        {row.days_since_followup ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {row.next_followup_date
                          ? dayjs(row.next_followup_date).format("DD MMM YYYY")
                          : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[220px]">
    <TruncatedText text={row.latest_remarks} label="Remarks" />
</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* fixed pagination footer */}
          <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {filtered.length === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '…'
                  ? <span key={`ellipsis-${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                  : <button key={p} onClick={() => setPage(p)}
                      className={cn(
                        "min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors",
                        p === page
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      )}
                    >{p}</button>
                )
              }

              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
            </div>
          </div>
        </div>
      )}
    </ContainerCard>
  );
};

export default PipelineActivityReport;
